import { action } from '@play-co/replicant';
import * as Inventory from '../inventory';
import createActions from '../config/actions/createActions';
import {
  match2EnergyCost,
  match2BoosterCatalog,
  Match2BoosterType,
  boosterNames,
  Match2PowerupType,
  match2PowerupCatalog,
  powerupNames,
  getMatch2PlayOnAppCooldownTimestampKey,
  getSocialContinueCooldownTimestampKey,
  getInviteFriendsTimestampKey,
} from './match2.ruleset';
import {
  getDailyBonusRemainingCooldown,
  getDailyBonusRewards,
  getExtraMovesFromFreeContinue,
  getMatch2ContinueConfig,
  getMatch2LevelRewards,
  getMatch2MaxLevel,
  isMatch2TimedRewardActive,
  isMatch2UnlockableUnlocked,
  shouldMatch2SpendEnergy,
} from './match2.getters';
import { createReportableError } from '../utils/errors';
import { BlockId } from 'src/game/minigame/match2-timestep/game/defs/block';
import * as Match2 from '.';
import { grantMatch2Rewards, setMatch2Timestamp } from './match2.modifiers';
import { duration } from '../utils/duration';
import { match2TournamentActions } from './tournament/match2-tournament.actions';
import * as match2TournamentGetters from './tournament/match2-tournament.getters';
import * as match2TournamentModifiers from './tournament/match2-tournament.modifiers';
import { match2DailyChallengesActions } from './dailychallenge/match2-dailychallenge.actions';
import { match2Cheats } from './match2.cheats';
import { match2RacesActions } from './races/races.actions';
import {
  canParticipateOnCurrentRace,
  getActiveRace,
  hasJoinedActiveRace,
} from './races/races.getters';
import { match2SaveStateActions } from './savestate/match2-savestate.actions';
import { match2BundleSaleActions } from './bundlesale/match2-bundlesale.actions';
import { addLeagueScore } from './leagues/leagues.modifiers';
import { leaguesActions } from './leagues/leagues.actions';
import { match2FrenzyActions } from 'src/replicant/match2/frenzy/match2-frenzy.actions';
import * as match2FrenzyModifiers from './frenzy/match2-frenzy.modifiers';
import {
  isRandomReward,
  isTimedReward,
} from './rewards/match2-rewards.ruleset';
import {
  getDailyBonusCooldownUntilMidnight,
  getProcessedMatch2Rewards,
} from './rewards/match2-rewards.getters';
import { match2MagicLampsActions } from './magiclamps/magiclamps.actions';
import { setCurrentRegion } from './regions/regions.modifiers';
import { getRegionId } from './regions/regions.getters';
import { regionsActions } from './regions/regions.actions';
import { getSharedStateActionsAPI } from '../config/actions/createSharedStatesActions';
import { socialContinueCooldown } from '../social/social.ruleset';
import {
  progressStreakBattleRoyale,
  setStreakBattleRoyaleStatus,
} from '../streakbattleroyale/streakbattleroyale.modifiers';
import { isStreakBattleRoyaleActive } from '../streakbattleroyale/streakbattleroyale.getters';
import { Feature } from '../analytics';
import { getRewardsId } from '../native/native.getters';
import {
  clearSuperLollipopWinStreak,
  increaseSuperLollipopWinStreak,
} from '../superLollipop/superLollipop.modifiers';
import { incrementPuzzleWinCount } from '../puzzleWinCount/puzzleWinCount.modifiers';
import { isWeeklyLeaderboardsAvailable } from '../weeklyLeaderboard/weeklyLeaderboard.getters';
import { weeklyLeaderboardPuzzleScore } from '../weeklyLeaderboard/weeklyLeaderboard.modifiers';
import { addTreasureExpressTickets } from '../treasureExpress/treasureExpress.modifiers';
import { isSuperLollipopActive } from '../superLollipop/superLollipop.getters';
import { resetPromoCooldown } from '../promo/promo.modifiers';
import { buddiesAddScoreByPuzzleAttempts } from '../buddies/buddies.modifiers';
import { hasJoinedLeague } from './leagues/leagues.getters';
import {
  cardsGetAnalyticsContextFromInventory,
  cardsGetAnalyticsInventory,
  cardsGetCardAnalyticsProperties,
  cardsGetCompletionAnalyticsEvents,
} from '../cards/cards.analytics';
import { cardsGetLevelWinDropSource } from '../cards/cards.getters';
import { sendAnalyticsEvents } from '../analytics';

const match2MiscAction = createActions({
  completeMatch2Puzzle: action(
    (
      state,
      {
        result,
        blocksBroken,
        stars,
        hasContinued,
        level,
      }: {
        result: 'win' | 'lose';
        blocksBroken: Partial<Record<BlockId, number>>;
        stars: number;
        hasContinued: boolean;
        level?: number;
      },
      rawApi,
    ) => {
      const api = getSharedStateActionsAPI(rawApi);
      const now = api.date.now();

      // Capture card-drop analytics inputs before rewards mutate inventory.
      const cardsInventoryBeforeRewards = cardsGetAnalyticsInventory(state);
      const cardDropSource = cardsGetLevelWinDropSource(state, {
        hasContinued,
      });
      const cardDropWinStreak = state.match2.winStreak + 1;
      const cardDropPuzzleLevel = level ?? state.match2.level;

      // Reset the playing state to false on gameplay complete
      state.match2.playing = false;

      // Delete existing save state, if any
      if (state.match2.saveState) {
        delete state.match2.saveState;
      }

      // Grant rewards, if any.
      const rewardSet = getMatch2LevelRewards(state, result, stars, now, {
        hasContinued,
      });
      Match2.modifiers.grantMatch2Rewards(
        state,
        rewardSet,
        { feature: 'match_level_completed' },
        api,
      );

      // Emit canonical card grants and completion transitions for puzzle drops.
      const cardRewards = rewardSet.filter((reward) => reward.type === 'card');
      if (cardRewards.length > 0 && cardDropSource) {
        const cardsInventoryAfterRewards = cardsGetAnalyticsInventory(state);
        const cardsContext = cardsGetAnalyticsContextFromInventory(
          cardsInventoryAfterRewards,
        );
        sendAnalyticsEvents(api, [
          ...cardRewards.map((reward) => ({
            eventType: 'CardClaim' as const,
            eventProperties: {
              cardID: reward.cardId,
              ...cardsGetCardAnalyticsProperties(reward.cardId),
              puzzleLevel: cardDropPuzzleLevel,
              winStreak: cardDropWinStreak,
              dropSource: cardDropSource,
              ...cardsContext,
            },
          })),
          ...cardsGetCompletionAnalyticsEvents(
            cardsInventoryBeforeRewards,
            cardsInventoryAfterRewards,
          ),
        ]);
      }

      // Set used continue last match flag.
      state.match2.usedContinueLastMatch = state.match2.continues > 0;

      if (result === 'lose') {
        state.match2.losingStreak++;
        state.match2.winStreak = 0;
        Match2.modifiers.resetBuruStreak(state);
        match2TournamentModifiers.resetEventMultiplier(state, now);

        // Set streak event status to 'failed', if event is active.
        if (isStreakBattleRoyaleActive(state, now)) {
          setStreakBattleRoyaleStatus(state, 'failed', now);
        }

        // Update the win ladder accordingly.
        Match2.rubberbandingModifiers.updateRubberbandingLadderStep(
          state,
          now,
          false, // always a loose here
          hasContinued,
        );

        // Only reset if streak was active
        if (isSuperLollipopActive(state)) {
          clearSuperLollipopWinStreak(state);
        }

        return { rewards: rewardSet, events: [] };
      }

      // Increment weekly leaderboard score based on puzzle stars.
      if (isWeeklyLeaderboardsAvailable(state, now)) {
        weeklyLeaderboardPuzzleScore(state, stars, now);
      }

      // Update treasure express score before resetting losing streak.
      addTreasureExpressTickets(state, now);

      // Add buddies score if feature is available
      const puzzleAttempts = state.match2.losingStreak + 1;
      buddiesAddScoreByPuzzleAttempts(state, { puzzleAttempts, now }, api);

      // Reset losing streak on win.
      state.match2.losingStreak = 0;

      // Increment win streak.
      state.match2.winStreak++;

      // Increment buruStreak if unlocked.
      if (isMatch2UnlockableUnlocked(state, 'buruStreak')) {
        state.match2.buruStreak.value++;
      }

      // Update puzzle win count
      incrementPuzzleWinCount(state, now);

      // Add to match2 frenzy progress
      const events = match2FrenzyModifiers.grantFrenzyPoints(
        state,
        now,
        blocksBroken,
        rewardSet,
      );

      // Refund energy only if frenzy energy reward is not active.
      if (shouldMatch2SpendEnergy(state, { level, now })) {
        Inventory.modifiers.addEnergy(
          state,
          match2EnergyCost,
          now,
          { feature: 'match2', $subFeature: 'level_win_refund' },
          api,
        );
      }

      // Update the win ladder accordingly.
      Match2.rubberbandingModifiers.updateRubberbandingLadderStep(
        state,
        now,
        true, // always a win here
        hasContinued,
      );

      // Increment super lollipop win streak, if the feature is available.
      // Should happen just before incrementing the level, so beating level 99 doesn't count.
      increaseSuperLollipopWinStreak(state);

      // Reset PN promo reminder after level 50 and 100
      // See: https://docs.google.com/document/d/12v0m7zCU5p5A4dun_ZMAU86fOn2KmFxEd_vKYl72toM/edit?usp=sharing
      if (level === 50 || level === 100) {
        resetPromoCooldown(state, 'pushNotificationReminderV2');
      }

      // Keep track of the current level.
      // Reached max level. Don't increment.
      const shouldIncrement = state.match2.level <= getMatch2MaxLevel(state);
      if (shouldIncrement) {
        state.match2.lastLevel = state.match2.level;
        state.match2.level += 1;
      }

      // Update master league score.
      if (!shouldIncrement) {
        addLeagueScore(state, 1, now);
      }

      // Update current region.
      setCurrentRegion(state, getRegionId(state));

      //  Update tournament state.
      if (match2TournamentGetters.canPlay(state, api.date.now())) {
        match2TournamentModifiers.createOrUpdateTournament(
          state,
          stars,
          api.date.now(),
        );
      }

      // Update streak event progress, if event is active.
      if (isStreakBattleRoyaleActive(state, now)) {
        progressStreakBattleRoyale(state, api.date.now());
      }

      // Update team competition score.
      if (state.team.currentTeamId) {
        api.sharedStates.team.postMessage.postEventScore(
          state.team.currentTeamId,
          {
            timestamp: api.date.now(),
            score: stars,
          },
        );
      }

      return { rewards: rewardSet, events };
    },
  ),

  /**
   * Discard an active puzzle interrupted by in-place recovery without applying
   * loss effects or modifying completed puzzle state.
   *
   * @returns Nothing.
   */
  abortMatch2Puzzle: action((state) => {
    // Preserve server-authoritative state when the puzzle already completed.
    if (!state.match2.playing) return;

    // Discard attempt counters so future continue pricing starts cleanly.
    state.match2.continues = 0;

    // Discard score that was only accumulated for the interrupted attempt.
    delete state.match2.pendingRaceScore;

    // Prevent the next puzzle entry from resuming the interrupted board.
    delete state.match2.saveState;

    // Prevent the following login cleanup from treating recovery as abandonment.
    state.match2.playing = false;
  }),

  startMatch2Puzzle: action((state, args: { level?: number }, api) => {
    state.match2.continues = 0;
    delete state.match2.pendingRaceScore;

    // Reset win ladder if needed.
    Match2.rubberbandingModifiers.resetRubberbandingLadderStep(
      state,
      api.date.now(),
    );

    const now = api.date.now();
    if (
      canParticipateOnCurrentRace(state, now) &&
      hasJoinedActiveRace(state, now)
    ) {
      const { id } = getActiveRace(now)!;
      state.match2.pendingRaceScore = {
        raceId: id,
        raceIndex: state.match2.races[id].length - 1,
        score: 0,
      };
    }

    // Set playing state to true when start playing
    state.match2.playing = true;
    state.match2.time = now;

    // Update current region.
    setCurrentRegion(state, getRegionId(state));

    // Spend energy cost on level start, if applicable.
    if (shouldMatch2SpendEnergy(state, { level: args.level, now })) {
      Inventory.modifiers.consumeEnergy(
        state,
        match2EnergyCost,
        api.date.now(),
        { feature: 'match2', $subFeature: 'level_play_tax' },
        api,
      );
    }
  }),

  /**
   * Resume match2 puzzle from save state, setting the playing flag back to true
   */
  resumeMatch2PuzzleFromSaveState: action((state, _: void, api) => {
    state.match2.playing = true;
  }),

  /**
   * If we detect that puzzle is brocken we will restart game and give back energy
   */
  match2RestartLevel: action(
    (state, selectedPowerups: Match2PowerupType[], api) => {
      const now = api.date.now();

      // Skip if free energy boost is active
      if (isMatch2TimedRewardActive(state, 'energy', api.date.now())) return;
      // Skip if not playing
      if (!state.match2.playing) return;

      // Give back one energy
      Inventory.modifiers.addEnergy(
        state,
        match2EnergyCost,
        api.date.now(),
        {
          feature: Feature.MATCH2._,
          $subFeature: Feature.MATCH2.BROKEN_PUZZLE,
        },
        api,
      );

      // Restore all selected powerups
      selectedPowerups.forEach((type) => {
        const { powerups } = state.match2;
        if (powerups && powerups[type] !== undefined) {
          powerups[type] += 1;
        }
      });
    },
  ),

  match2ContinueTutorial: action((state, _: void, api) => {
    // ensure tutorial incomplete
    state.match2.tutorialComplete = false;

    // ensure puzzle level 1
    state.match2.level = 1;
  }),

  match2CompleteTutorial: action((state, _: void, api) => {
    // mark tutorial complete
    state.match2.tutorialComplete = true;

    // set puzzle level 2
    state.match2.level = 2;
    state.match2.lastLevel = 1;
  }),

  continueMatch2Puzzle: action((state, _: void, api) => {
    // Continues are valid for either main-map or active Dream Drift puzzles.
    const dreamDriftPuzzlePlaying =
      (state.dreamDrift?.instance?.playing ?? 0) > 0;
    if (!state.match2.playing && !dreamDriftPuzzlePlaying) {
      api.reportError(
        createReportableError(
          'warning',
          new Error('Cannot continue completed Match2 puzzle'),
        ),
        {
          puzzleLevel: state.match2.level,
          continues: state.match2.continues,
        },
      );
      return;
    }

    const config = getMatch2ContinueConfig(state);
    if (config.price > Inventory.getters.getGems(state)) {
      throw new Error('Not enough gems to purchase continue.');
    }

    Inventory.modifiers.consumeGems(
      state,
      config.price,
      {
        feature: 'match_continue',
        $subFeature: `continue_${state.match2.continues}`,
      },
      api,
    );
    state.match2.continues += 1;
  }),

  continueMatch2PuzzleViaTos: action((state) => {
    state.match2.continues += 1;
  }),

  consumeFreeContinue: action((state) => {
    const amount = getExtraMovesFromFreeContinue(state);
    state.match2.inventory.extraMoves -= amount;
  }),

  purchaseMatch2Booster: action(
    (state, { type }: { type: Match2BoosterType }, api) => {
      const { boosters } = state.match2;
      if (!boosters[type]) {
        boosters[type] = 0;
      }

      const { price, quantity } = match2BoosterCatalog[type];

      Inventory.modifiers.consumeGems(
        state,
        price,
        {
          feature: 'buy_boosters',
          $subFeature: boosterNames[type],
        },
        api,
      );
      boosters[type] += quantity;
    },
  ),

  purchaseMatch2Powerup: action(
    (state, { type }: { type: Match2PowerupType }, api) => {
      const { powerups } = state.match2;

      if (!powerups[type]) {
        powerups[type] = 0;
      }

      const { price, quantity } = match2PowerupCatalog[type];

      Inventory.modifiers.consumeGems(
        state,
        price,
        {
          feature: 'buy_power_blocks',
          $subFeature: powerupNames[type],
        },
        api,
      );
      powerups[type] += quantity;
    },
  ),

  consumeMatch2Booster: action(
    (state, args: { type: Match2BoosterType }, api) => {
      const { type } = args;
      const { boosters } = state.match2;
      if (!boosters[type]) {
        // Not necessarily an error due to Match2 state being
        // stored in local storage, but still report it for o11y
        api.reportError(
          createReportableError(
            'warning',
            new Error(`No ${type} boosters remaining.`),
          ),
        );
        return false;
      }

      boosters[type] -= 1;
      return true;
    },
  ),

  consumeMatch2Powerup: action(
    (state, args: { type: Match2PowerupType }, api) => {
      const { type } = args;
      const { powerups } = state.match2;
      if (!powerups[type]) {
        // Not necessarily an error due to Match2 state being
        // stored in local storage, but still report it for o11y
        api.reportError(
          createReportableError(
            'warning',
            new Error(`No ${type} powerups remaining.`),
          ),
        );
        return false;
      }

      powerups[type] -= 1;
      return true;
    },
  ),

  /**
   * Collect daily bonus reward and reset timer
   */
  collectDailyBonusRewards: action((state, _: void, api) => {
    const now = api.date.now();

    // Exit if daily bonus is not yet ready
    if (getDailyBonusRemainingCooldown(state, now) > 0) return;

    const rewards = getDailyBonusRewards(state);
    const dailyCollected = state.match2.dailyBonus.dailyRewardsCollected;
    const totalCollected = state.match2.dailyBonus.totalRewardsCollected;

    Match2.modifiers.grantMatch2Rewards(
      state,
      rewards,
      { feature: 'daily_bonus' },
      api,
    );

    for (let index = 0; index < rewards.length; index++) {
      const reward = rewards[index];
      if (isRandomReward(reward) || isTimedReward(reward)) continue;

      if (reward.type === 'boosters' || reward.type === 'powerups') {
        if (!dailyCollected[reward.type][reward.subType])
          dailyCollected[reward.type][reward.subType] = 0;
        if (!totalCollected[reward.type][reward.subType])
          totalCollected[reward.type][reward.subType] = 0;
        dailyCollected[reward.type][reward.subType] += reward.amount;
        totalCollected[reward.type][reward.subType] += reward.amount;
      } else if (
        reward.type === 'bPoints' ||
        reward.type === 'gems' ||
        reward.type === 'energy' ||
        reward.type === 'moves'
      ) {
        const type = reward.type === 'bPoints' ? 'points' : reward.type;
        dailyCollected[type] += reward.amount;
        totalCollected[type] += reward.amount;
      }
    }

    // Get cooldown to until midnight to reset the daily bonus
    const dailyBonusCooldown = getDailyBonusCooldownUntilMidnight(now);

    state.match2.dailyBonus.dailyCount++;
    state.match2.dailyBonus.totalCount++;
    state.match2.dailyBonus.nextBonusTimestamp = now + dailyBonusCooldown;

    // The daily count is reset on login.
    // First collection of the day should schedule the next daily bonus message.
    // The message should only be scheduled once a day.
    api.scheduledActions.schedule.sendDailyBonusMessage({
      args: { subFeature: 'notification_daily_bonus' },
      notificationId: 'notification_daily_bonus',
      delayInMS: dailyBonusCooldown,
    });
  }),

  tipComplete: action((state, options: { id: string }) => {
    state.match2.tips[options.id] = true;
  }),

  claimMatch2InstallReward: action((state, timestamp: number, api) => {
    // If reward state is in received give reward
    if (state.native.appInstallRewardStatus === 'received') {
      // Give reward
      const rewards = getProcessedMatch2Rewards(getRewardsId(state), timestamp);
      grantMatch2Rewards(state, rewards, { feature: 'native_install' }, api);
      // Switch to claimed state
      state.native.appInstallRewardStatus = 'claimed';
    }
  }),

  setMatch2PlayOnAppCooldownTimestamp: action((state, _: void, api) => {
    setMatch2Timestamp(
      state,
      getMatch2PlayOnAppCooldownTimestampKey(),
      api.date.now() + duration({ hours: 24 }),
    );
  }),

  setInviteCooldownTimestamp: action((state, _: void, api) => {
    setMatch2Timestamp(
      state,
      getInviteFriendsTimestampKey(),
      api.date.now() + duration({ hours: 24 }),
    );
  }),

  setMatch2SocialContinueCooldownTimestamp: action((state, _: void, api) => {
    setMatch2Timestamp(
      state,
      getSocialContinueCooldownTimestampKey(),
      api.date.now() + socialContinueCooldown,
    );
  }),
});

export const match2Actions = {
  ...match2MiscAction,
  ...match2FrenzyActions,
  ...match2TournamentActions,
  ...match2DailyChallengesActions,
  ...match2RacesActions,
  ...match2SaveStateActions,
  ...match2BundleSaleActions,
  ...leaguesActions,
  ...match2MagicLampsActions,
  ...regionsActions,

  // Cheats are only available in development mode, not allowed in production
  ...(process.env.IS_DEVELOPMENT ? match2Cheats : ({} as typeof match2Cheats)),
};
